import { useState } from "react";
import { Building2, CalendarDays, CheckCircle2, FileText, GraduationCap, Landmark, Lightbulb, LineChart, MessageSquare, Send, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { SiteLayout } from "@/components/SiteChrome";
import { OpsNav } from "@/components/OpsNav";
import { MarketingCalendar } from "@/components/MarketingCalendar";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { startLogin } from "@/const";

/**
 * The staff client engagement desk.
 *
 * Reads from listClients, which is a different function from the client-facing
 * dashboard on purpose - see the note in server/clients/store.ts.
 */

const STATUSES = ["registered", "in_review", "contracting", "awaiting_payment", "active", "paused", "completed", "declined"] as const;

const money = (cents: number | null | undefined) =>
  cents === null || cents === undefined
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(cents / 100);

const date = (value: string | Date | null | undefined) =>
  value ? new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

export default function ClientOperations() {
  const { user, loading } = useAuth();
  const utils = trpc.useUtils();
  const isAdmin = Boolean(user && user.role === "admin");
  const clients = trpc.clientPortal.adminList.useQuery(undefined, { enabled: isAdmin });
  const [open, setOpen] = useState<number | null>(null);

  const refresh = () => void utils.clientPortal.adminList.invalidate();
  const onError = (error: { message: string }) => toast.error("That did not save", { description: error.message });

  const approve = trpc.clientPortal.adminApprove.useMutation({
    onSuccess: result => {
      toast.success(result.seeded ? `Engagement opened — ${result.seeded} milestones seeded.` : "Engagement already has a plan.");
      refresh();
    },
    onError,
  });
  const setStatus = trpc.clientPortal.adminSetStatus.useMutation({ onSuccess: refresh, onError });
  const documentKinds = trpc.clientPortal.adminDocumentKinds.useQuery(undefined, { enabled: isAdmin });
  const issuePack = trpc.clientPortal.adminIssueStandardPack.useMutation({
    onSuccess: result => { toast.success(`${result.issued.length} document(s) sent for signature.`); refresh(); },
    onError,
  });
  const issueOne = trpc.clientPortal.adminIssueDocument.useMutation({
    onSuccess: () => { toast.success("Document sent for signature."); refresh(); },
    onError,
  });
  const autopayLink = trpc.clientPortal.adminAutopayLink.useMutation({
    onSuccess: result => {
      // Copied rather than opened: opening it here would start the flow in a
      // staff browser, and the mandate has to be agreed by the client.
      void navigator.clipboard?.writeText(result.url).catch(() => undefined);
      toast.success("Autopay link copied", { description: "Send it to the client — it starts their Stripe mandate, so do not complete it yourself." });
    },
    onError,
  });

  if (loading) return <SiteLayout><div className="ops-page"><OpsNav /><div className="site-container cp-ops-loading">Loading…</div></div></SiteLayout>;

  if (!user) {
    return (
      <SiteLayout>
        <div className="ops-page">
          <OpsNav />
          <section className="ops-access">
            <p className="ops-overline">Private workspace</p>
            <h1>Sign in to open the client desk.</h1>
            <button className="button button--coral" onClick={() => startLogin()}>Sign in</button>
          </section>
        </div>
      </SiteLayout>
    );
  }

  if (user.role !== "admin") {
    return (
      <SiteLayout>
        <div className="ops-page">
          <OpsNav />
          <section className="ops-access">
            <p className="ops-overline">Private workspace</p>
            <h1>This workspace is restricted.</h1>
            <p>Your account is authenticated but does not have client administration permission.</p>
          </section>
        </div>
      </SiteLayout>
    );
  }

  const rows = clients.data ?? [];

  return (
    <SiteLayout>
      <div className="ops-page">
        <OpsNav />
        <section className="ops-hero">
          <div className="site-container">
            <p className="eyebrow eyebrow--light">Client engagements</p>
            <h1>Engagement desk.</h1>
            <p>Registrations, engagement status, contract terms and account standing.</p>
          </div>
        </section>

        <section className="section">
          <div className="site-container">
            {clients.isLoading && <p>Loading engagements…</p>}
            {!clients.isLoading && rows.length === 0 && <p className="cp-empty">No client registrations yet.</p>}

            {rows.map(row => (
              <article key={row.id} className="cp-ops-row" data-standing={row.standingStage}>
                <div className="cp-ops-row__main">
                  <div className="cp-ops-row__head">
                    <Building2 size={16} />
                    <div>
                      <strong>{row.companyName}</strong>
                      <small>{row.reference} · {row.engagementType}</small>
                    </div>
                    {/* Two independent signals. A client can be fully paid up
                        and still blocked on autopay, or suspended mid-delivery -
                        collapsing them into one badge hides whichever is worse. */}
                    <span className="cp-pill" data-kind="status">{row.status.replace(/_/g, " ")}</span>
                    {row.standingStage !== "current" && (
                      <span className="cp-pill" data-kind="standing">{row.standingLabel}</span>
                    )}
                    {!row.paymentMethodVerified && <span className="cp-pill" data-kind="gate">No autopay</span>}
                  </div>

                  <dl className="cp-ops-facts">
                    <div><dt>Progress</dt><dd>{row.overallPercent}% · {row.currentPhase}</dd></div>
                    <div><dt>Contract</dt><dd>{money(row.contractValueCents)}</dd></div>
                    <div><dt>Term</dt><dd>{date(row.contractStartAt)} – {date(row.contractEndAt)}</dd></div>
                    <div><dt>Outstanding</dt><dd>{money(row.outstandingCents)}{row.pastDueCents > 0 ? ` (${money(row.pastDueCents)} late)` : ""}</dd></div>
                  </dl>

                  <button type="button" className="cp-ops-toggle" onClick={() => setOpen(open === row.id ? null : row.id)}>
                    {open === row.id ? "Hide users" : `Show ${row.users.length} user${row.users.length === 1 ? "" : "s"}`}
                  </button>

                  {open === row.id && (
                    <ul className="cp-ops-users">
                      {row.users.map(u => (
                        <li key={u.id}>
                          <div><strong>{u.name}</strong><small>{u.email} · {u.role}</small></div>
                          <em data-ok={u.twoFactorReady || undefined}>
                            {u.disabled ? "Disabled" : u.twoFactorReady ? "2FA active" : "2FA not set up"}
                          </em>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="cp-ops-row__actions">
                  {!row.milestonesSeeded && (
                    <button
                      className="button button--coral"
                      disabled={approve.isPending}
                      onClick={() => approve.mutate({ clientId: row.id })}
                    >
                      <CheckCircle2 size={15} />Open engagement
                    </button>
                  )}
                  <label className="cp-ops-status">
                    <span>Status</span>
                    <select
                      value={row.status}
                      disabled={setStatus.isPending}
                      onChange={event => setStatus.mutate({ clientId: row.id, status: event.target.value as (typeof STATUSES)[number] })}
                    >
                      {STATUSES.map(status => (
                        <option key={status} value={status}>{status.replace(/_/g, " ")}</option>
                      ))}
                    </select>
                  </label>
                  {/* Hidden when no template is configured, and also when
                      issuing is blocked - a sandbox key can resolve a template
                      and still must not send to a client. The Documents tab
                      explains why and offers the test instead. */}
                  {(documentKinds.data?.configured.length ?? 0) > 0 && documentKinds.data?.canIssue && (
                    <>
                      <button
                        type="button"
                        className="button button--outline-dark"
                        disabled={issuePack.isPending}
                        onClick={() => issuePack.mutate({ clientId: row.id, engagementType: row.engagementType })}
                      >
                        <FileText size={14} />{issuePack.isPending ? "Sending…" : "Send document pack"}
                      </button>
                      {documentKinds.data?.configured.includes("poa") && (
                        <button
                          type="button"
                          className="button button--outline-dark"
                          disabled={issueOne.isPending}
                          /* Separate and deliberate. A power of attorney is
                             never part of the standard pack. */
                          onClick={() => {
                            if (window.confirm(`Issue a Limited Power of Attorney to ${row.companyName}? This grants agency authority and should only be sent when the engagement requires it.`)) {
                              issueOne.mutate({ clientId: row.id, kind: "poa" });
                            }
                          }}
                        >
                          <FileText size={14} />Issue POA
                        </button>
                      )}
                    </>
                  )}
                  <UpdateForm clientId={row.id} onPosted={refresh} />
                  {/* The hosted setup link, so it can be pasted into an email
                      or read out on a call. It expires on Stripe's side, which
                      is why it is fetched on demand rather than stored. */}
                  <button
                    type="button"
                    className="button button--outline-dark"
                    disabled={autopayLink.isPending}
                    onClick={() => autopayLink.mutate({ clientId: row.id })}
                  >
                    <Landmark size={14} />Autopay link
                  </button>
                </div>

                <ClientDesk clientId={row.id} companyName={row.companyName} />
              </article>
            ))}
          </div>
        </section>

        {/* Below the real engagements on purpose: the desk's first screen
            should be the actual book of business, not the training set. */}
        <TrainingPanel />
      </div>
    </SiteLayout>
  );
}

/** Post an update to a client's dashboard. */
function UpdateForm({ clientId, onPosted }: { clientId: number; onPosted: () => void }) {
  const [open, setOpen] = useState(false);
  const post = trpc.clientPortal.adminPostUpdate.useMutation({
    onSuccess: () => { toast.success("Update posted to the client dashboard."); setOpen(false); onPosted(); },
    onError: error => toast.error("Not posted", { description: error.message }),
  });

  if (!open) {
    return <button type="button" className="button button--outline-dark" onClick={() => setOpen(true)}><Send size={14} />Post update</button>;
  }

  return (
    <form
      className="cp-ops-update"
      onSubmit={event => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        post.mutate({
          clientId,
          title: String(data.get("title") ?? "").trim(),
          body: String(data.get("body") ?? "").trim(),
        });
      }}
    >
      <label className="field"><span>Title</span><input name="title" required maxLength={200} autoFocus /></label>
      <label className="field"><span>Update</span><textarea name="body" required rows={4} maxLength={8000} /></label>
      <div className="cp-ops-update__actions">
        <button className="button button--coral" disabled={post.isPending}>{post.isPending ? "Posting…" : "Post"}</button>
        <button type="button" className="button button--outline-dark" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* The engagement desk: calendar, suggestions, notes, financials              */
/* -------------------------------------------------------------------------- */

type DeskTab = "documents" | "calendar" | "suggestions" | "notes" | "financials" | "collections";

/**
 * Everything staff do inside one engagement, behind a tab strip.
 *
 * Collapsed by default and only queried once opened. Four extra queries per
 * client row would mean a desk with twenty engagements firing eighty requests
 * on load, most of them for panels nobody looked at.
 */
function ClientDesk({ clientId, companyName }: { clientId: number; companyName: string }) {
  const [tab, setTab] = useState<DeskTab | null>(null);

  return (
    <div className="cp-desk">
      <div className="cp-desk__tabs" role="tablist" aria-label={`${companyName} workspace`}>
        <button type="button" role="tab" aria-selected={tab === "documents"} onClick={() => setTab(tab === "documents" ? null : "documents")}>
          <FileText size={14} />Documents
        </button>
        <button type="button" role="tab" aria-selected={tab === "calendar"} onClick={() => setTab(tab === "calendar" ? null : "calendar")}>
          <CalendarDays size={14} />Calendar
        </button>
        <button type="button" role="tab" aria-selected={tab === "suggestions"} onClick={() => setTab(tab === "suggestions" ? null : "suggestions")}>
          <Lightbulb size={14} />Suggestions
        </button>
        <button type="button" role="tab" aria-selected={tab === "notes"} onClick={() => setTab(tab === "notes" ? null : "notes")}>
          <MessageSquare size={14} />Notes
        </button>
        <button type="button" role="tab" aria-selected={tab === "financials"} onClick={() => setTab(tab === "financials" ? null : "financials")}>
          <LineChart size={14} />Financials
        </button>
        <button type="button" role="tab" aria-selected={tab === "collections"} onClick={() => setTab(tab === "collections" ? null : "collections")}>
          <ShieldAlert size={14} />Collections
        </button>
      </div>

      {tab === "documents" && <DeskDocuments clientId={clientId} />}
      {tab === "calendar" && <DeskCalendar clientId={clientId} />}
      {tab === "suggestions" && <DeskSuggestions clientId={clientId} />}
      {tab === "notes" && <DeskNotes clientId={clientId} />}
      {tab === "financials" && <DeskFinancials clientId={clientId} />}
      {tab === "collections" && <DeskCollections clientId={clientId} companyName={companyName} />}
    </div>
  );
}

function DeskCalendar({ clientId }: { clientId: number }) {
  const utils = trpc.useUtils();
  const query = trpc.clientPortal.adminCampaigns.useQuery({ clientId });
  const refresh = () => void utils.clientPortal.adminCampaigns.invalidate({ clientId });
  const onError = (error: { message: string }) => toast.error("That did not save", { description: error.message });

  const save = trpc.clientPortal.adminSaveCampaign.useMutation({ onSuccess: refresh, onError });
  const update = trpc.clientPortal.adminUpdateCampaign.useMutation({ onSuccess: refresh, onError });
  const remove = trpc.clientPortal.adminDeleteCampaign.useMutation({ onSuccess: refresh, onError });

  if (query.isLoading) return <p className="cp-desk__loading">Loading calendar…</p>;

  return (
    <div className="cp-desk__panel">
      <MarketingCalendar
        campaigns={query.data?.campaigns ?? []}
        editable
        busy={save.isPending || update.isPending || remove.isPending}
        onCreate={draft => save.mutate({ ...draft, clientId })}
        onUpdate={(id, patch) => update.mutate({ id, clientId, patch })}
        onDelete={id => remove.mutate({ id, clientId })}
      />
    </div>
  );
}

function DeskSuggestions({ clientId }: { clientId: number }) {
  const utils = trpc.useUtils();
  const query = trpc.clientPortal.adminSuggestions.useQuery({ clientId });
  const refresh = () => void utils.clientPortal.adminSuggestions.invalidate({ clientId });
  const onError = (error: { message: string }) => toast.error("That did not save", { description: error.message });

  const post = trpc.clientPortal.adminPostSuggestion.useMutation({
    onSuccess: () => { toast.success("Suggestion posted to the client dashboard."); refresh(); },
    onError,
  });
  const implemented = trpc.clientPortal.adminMarkSuggestionImplemented.useMutation({ onSuccess: refresh, onError });

  return (
    <div className="cp-desk__panel">
      <form
        className="cp-desk__form"
        onSubmit={event => {
          event.preventDefault();
          const form = event.currentTarget;
          const data = new FormData(form);
          post.mutate({
            clientId,
            title: String(data.get("title") ?? "").trim(),
            body: String(data.get("body") ?? "").trim(),
            category: String(data.get("category")) as "marketing",
            priority: String(data.get("priority")) as "normal",
          });
          form.reset();
        }}
      >
        <label className="field"><span>Suggestion</span><input name="title" required maxLength={200} placeholder="e.g. Move the 6am class to 5:45" /></label>
        <div className="cp-desk__form-row">
          <label className="field">
            <span>Area</span>
            <select name="category" defaultValue="marketing">
              <option value="marketing">Marketing</option>
              <option value="operations">Operations</option>
              <option value="retention">Retention</option>
              <option value="revenue">Revenue</option>
              <option value="facility">Facility</option>
              <option value="staffing">Staffing</option>
              <option value="other">General</option>
            </select>
          </label>
          <label className="field">
            <span>Priority</span>
            <select name="priority" defaultValue="normal">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </label>
        </div>
        <label className="field"><span>Detail</span><textarea name="body" required rows={3} maxLength={8000} /></label>
        <button className="button button--coral" disabled={post.isPending}>{post.isPending ? "Posting…" : "Post suggestion"}</button>
      </form>

      {query.isLoading && <p className="cp-desk__loading">Loading…</p>}
      {query.data?.suggestions.length === 0 && <p className="cp-empty">None posted yet.</p>}
      <ul className="cp-desk__list">
        {(query.data?.suggestions ?? []).map(suggestion => (
          <li key={suggestion.id} data-status={suggestion.status}>
            <div>
              <strong>{suggestion.title}</strong>
              <small>{suggestion.category} · {suggestion.priority} · {suggestion.status}</small>
              {suggestion.clientResponse && <blockquote>{suggestion.clientResponse}</blockquote>}
            </div>
            {/* Only Altitude may mark something implemented. A client saying
                they did it and us confirming it are different claims. */}
            {suggestion.status === "accepted" && (
              <button
                type="button"
                className="button button--outline-dark"
                disabled={implemented.isPending}
                onClick={() => implemented.mutate({ id: suggestion.id })}
              >
                Mark implemented
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DeskNotes({ clientId }: { clientId: number }) {
  const utils = trpc.useUtils();
  const query = trpc.clientPortal.adminNotes.useQuery({ clientId });
  const post = trpc.clientPortal.adminPostNote.useMutation({
    onSuccess: () => { toast.success("Note saved."); void utils.clientPortal.adminNotes.invalidate({ clientId }); },
    onError: error => toast.error("Not saved", { description: error.message }),
  });

  return (
    <div className="cp-desk__panel">
      <form
        className="cp-desk__form"
        onSubmit={event => {
          event.preventDefault();
          const form = event.currentTarget;
          const data = new FormData(form);
          post.mutate({
            clientId,
            body: String(data.get("body") ?? "").trim(),
            visibility: String(data.get("visibility")) as "internal" | "shared",
          });
          form.reset();
        }}
      >
        <label className="field"><span>Note</span><textarea name="body" required rows={3} maxLength={8000} /></label>
        {/* Internal is the default, and the control says plainly what shared
            means. The client-facing read path filters on this column and
            nothing else, so this radio is the whole decision. */}
        <fieldset className="cp-desk__visibility">
          <legend>Who sees this</legend>
          <label><input type="radio" name="visibility" value="internal" defaultChecked /><span>Internal only</span></label>
          <label><input type="radio" name="visibility" value="shared" /><span>Visible to the client</span></label>
        </fieldset>
        <button className="button button--coral" disabled={post.isPending}>{post.isPending ? "Saving…" : "Save note"}</button>
      </form>

      {query.isLoading && <p className="cp-desk__loading">Loading…</p>}
      <ul className="cp-desk__list">
        {(query.data?.notes ?? []).map(note => (
          <li key={note.id}>
            <div>
              <strong>{note.authorName}</strong>
              <small>
                {date(note.createdAt)} · {note.visibility === "shared" ? "shared with client" : "internal"}
                {note.authorClientUserId ? " · from the client" : ""}
              </small>
              <p>{note.body}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Dollars in the form, cents in the database. The conversion happens once,
 *  here, because a cents field in a staff form is how a $4,500 month gets
 *  entered as $45. */
const centsFrom = (value: FormDataEntryValue | null): number | null => {
  const text = String(value ?? "").trim().replace(/[$,]/g, "");
  if (!text) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
};

const intFrom = (value: FormDataEntryValue | null): number | null => {
  const text = String(value ?? "").trim();
  if (!text) return null;
  const parsed = Number(text);
  return Number.isInteger(parsed) ? parsed : null;
};

function DeskFinancials({ clientId }: { clientId: number }) {
  const utils = trpc.useUtils();
  const query = trpc.clientPortal.adminFinancials.useQuery({ clientId });
  const refresh = () => void utils.clientPortal.adminFinancials.invalidate({ clientId });
  const onError = (error: { message: string }) => toast.error("That did not save", { description: error.message });

  const record = trpc.clientPortal.adminRecordFinancialPeriod.useMutation({
    onSuccess: () => { toast.success("Period saved."); refresh(); },
    onError,
  });
  const remove = trpc.clientPortal.adminDeleteFinancialPeriod.useMutation({ onSuccess: refresh, onError });

  return (
    <div className="cp-desk__panel">
      <form
        className="cp-desk__form"
        onSubmit={event => {
          event.preventDefault();
          const form = event.currentTarget;
          const data = new FormData(form);
          const start = String(data.get("periodStart") ?? "");
          const end = String(data.get("periodEnd") ?? "");
          if (!start || !end) return;
          record.mutate({
            clientId,
            // Noon UTC rather than midnight: a date input gives a bare day, and
            // a midnight-UTC instant renders as the previous day for anyone
            // behind UTC, which would label September's figures as August.
            periodStart: new Date(`${start}T12:00:00Z`),
            periodEnd: new Date(`${end}T12:00:00Z`),
            revenueCents: centsFrom(data.get("revenue")),
            expensesCents: centsFrom(data.get("expenses")),
            activeMembers: intFrom(data.get("activeMembers")),
            newMembers: intFrom(data.get("newMembers")),
            cancellations: intFrom(data.get("cancellations")),
            notes: String(data.get("notes") ?? "").trim() || null,
          });
          form.reset();
        }}
      >
        <div className="cp-desk__form-row">
          <label className="field"><span>Period start</span><input type="date" name="periodStart" required /></label>
          <label className="field"><span>Period end</span><input type="date" name="periodEnd" required /></label>
        </div>
        <div className="cp-desk__form-row">
          <label className="field"><span>Revenue ($)</span><input name="revenue" inputMode="decimal" placeholder="48500" /></label>
          <label className="field"><span>Expenses ($)</span><input name="expenses" inputMode="decimal" placeholder="39200" /></label>
        </div>
        <div className="cp-desk__form-row">
          <label className="field"><span>Active members</span><input name="activeMembers" inputMode="numeric" /></label>
          <label className="field"><span>Joins</span><input name="newMembers" inputMode="numeric" /></label>
          <label className="field"><span>Cancels</span><input name="cancellations" inputMode="numeric" /></label>
        </div>
        <label className="field"><span>Notes</span><input name="notes" maxLength={4000} /></label>
        <button className="button button--coral" disabled={record.isPending}>
          {record.isPending ? "Saving…" : "Save period"}
        </button>
        <p className="cp-desk__hint">
          Re-entering a period with the same start date replaces it, so correcting a month
          does not leave two of them on the client's trend line.
        </p>
      </form>

      {query.isLoading && <p className="cp-desk__loading">Loading…</p>}
      {query.data?.periods.length === 0 && <p className="cp-empty">No periods recorded yet.</p>}
      <ul className="cp-desk__list">
        {(query.data?.periods ?? []).map(period => (
          <li key={period.id}>
            <div>
              <strong>{date(period.periodStart)} – {date(period.periodEnd)}</strong>
              <small>
                {money(period.revenueCents)} revenue · {money(period.expensesCents)} expenses ·{" "}
                {period.activeMembers ?? "—"} members · {period.source}
              </small>
              {period.notes && <p>{period.notes}</p>}
            </div>
            <button
              type="button"
              className="button button--outline-dark"
              disabled={remove.isPending}
              onClick={() => { if (window.confirm("Delete this period?")) remove.mutate({ id: period.id }); }}
            >
              Delete
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

const DOC_KINDS = [
  { value: "nda", label: "Mutual NDA" },
  { value: "management_agreement", label: "Management agreement" },
  { value: "consulting_agreement", label: "Consulting agreement" },
  { value: "poa", label: "Limited power of attorney" },
  { value: "w9", label: "Form W-9" },
  { value: "addendum", label: "Addendum" },
  { value: "other", label: "Other" },
] as const;

const DOC_STATUSES = ["draft", "sent", "viewed", "completed", "declined", "voided", "expired"] as const;

/**
 * Documents for one engagement.
 *
 * Two kinds of row live here. Ones the PandaDoc API issued keep themselves
 * current from the webhook and are read-only: offering an edit would mean a
 * status that silently reverts on the next delivery. Ones recorded by hand are
 * editable, and labelled as such, because their accuracy depends entirely on
 * someone keeping them up to date.
 */
function DeskDocuments({ clientId }: { clientId: number }) {
  const utils = trpc.useUtils();
  const query = trpc.clientPortal.adminDocuments.useQuery({ clientId });
  const kinds = trpc.clientPortal.adminDocumentKinds.useQuery();
  const refresh = () => void utils.clientPortal.adminDocuments.invalidate({ clientId });
  const onError = (error: { message: string }) => toast.error("That did not save", { description: error.message });

  const record = trpc.clientPortal.adminRecordDocument.useMutation({
    onSuccess: () => { toast.success("Document recorded."); refresh(); },
    onError,
  });
  const setStatus = trpc.clientPortal.adminSetDocumentStatus.useMutation({ onSuccess: refresh, onError });
  const remove = trpc.clientPortal.adminRemoveDocument.useMutation({ onSuccess: refresh, onError });
  const sandboxTest = trpc.clientPortal.adminSandboxTest.useMutation({
    onSuccess: result => {
      toast.success(`Test document sent to ${result.sentTo}`, {
        description: `${result.tokensSent.length} tokens filled. It is watermarked — check for any [Token.Name] still showing, then void it.`,
      });
    },
    onError,
  });

  const canIssue = kinds.data?.canIssue ?? false;
  const configured = kinds.data?.configured ?? [];

  return (
    <div className="cp-desk__panel">
      {/* Says plainly why the automatic path is unavailable, rather than
          leaving a dead button or no explanation. */}
      {!canIssue && kinds.data?.blockedReason && (
        <p className="cp-desk__banner">{kinds.data.blockedReason}</p>
      )}

      {/* The sandbox test is offered only when a template exists but sending
          is blocked — which is exactly the sandbox-key situation. */}
      {!canIssue && configured.length > 0 && (
        <div className="cp-desk__sandbox">
          <strong>Test the integration</strong>
          <p>
            Sends a watermarked copy to Altitude's own address with this client's real data,
            so you can check every token resolves before a production key goes in. Nothing is
            recorded against the engagement.
          </p>
          <div className="cp-desk__sandbox-actions">
            {configured.map(kind => (
              <button
                key={kind}
                type="button"
                className="button button--outline-dark"
                disabled={sandboxTest.isPending}
                onClick={() => sandboxTest.mutate({ clientId, kind: kind as "nda" })}
              >
                Test {DOC_KINDS.find(k => k.value === kind)?.label ?? kind}
              </button>
            ))}
          </div>
        </div>
      )}

      <form
        className="cp-desk__form"
        onSubmit={event => {
          event.preventDefault();
          const form = event.currentTarget;
          const data = new FormData(form);
          const url = String(data.get("externalUrl") ?? "").trim();
          record.mutate({
            clientId,
            kind: String(data.get("kind")) as "nda",
            name: String(data.get("name") ?? "").trim() || undefined,
            status: String(data.get("status")) as "sent",
            externalUrl: url || null,
            requiredForOnboarding: data.get("required") === "on",
          });
          form.reset();
        }}
      >
        <div className="cp-desk__form-row">
          <label className="field">
            <span>Document</span>
            <select name="kind" defaultValue="nda">
              {DOC_KINDS.map(kind => <option key={kind.value} value={kind.value}>{kind.label}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Status</span>
            <select name="status" defaultValue="sent">
              {DOC_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
            </select>
          </label>
        </div>
        <label className="field">
          <span>Name <em>optional — defaults to the standard title</em></span>
          <input name="name" maxLength={200} />
        </label>
        <label className="field">
          <span>Link <em>optional — PandaDoc share link or stored PDF</em></span>
          <input name="externalUrl" type="url" maxLength={2000} placeholder="https://" />
        </label>
        <label className="cp-desk__check">
          {/* Checked by default: most documents hold up onboarding, and the
              one that does not (the POA) is the exception the server applies
              when this is left alone. */}
          <input type="checkbox" name="required" defaultChecked />
          <span>Required before services begin</span>
        </label>
        <button className="button button--coral" disabled={record.isPending}>
          {record.isPending ? "Saving…" : "Record document"}
        </button>
      </form>

      {query.isLoading && <p className="cp-desk__loading">Loading…</p>}
      {query.data?.documents.length === 0 && <p className="cp-empty">No documents recorded yet.</p>}
      <ul className="cp-desk__list">
        {(query.data?.documents ?? []).map(doc => (
          <li key={doc.id} data-status={doc.status === "completed" ? "implemented" : undefined}>
            <div>
              <strong>{doc.name}</strong>
              <small>
                {doc.kind.replace(/_/g, " ")} · {doc.source === "manual" ? "recorded by hand" : "PandaDoc"}
                {doc.sentAt ? ` · sent ${date(doc.sentAt)}` : ""}
                {doc.completedAt ? ` · signed ${date(doc.completedAt)}` : ""}
                {doc.requiredForOnboarding ? " · required" : " · optional"}
              </small>
              {doc.externalUrl && (
                <a href={doc.externalUrl} target="_blank" rel="noreferrer">Open document</a>
              )}
            </div>
            {doc.source === "manual" ? (
              <div className="cp-desk__doc-actions">
                <label>
                  <span className="sr-only">Status for {doc.name}</span>
                  <select
                    value={doc.status}
                    disabled={setStatus.isPending}
                    onChange={event => setStatus.mutate({ id: doc.id, status: event.target.value as "sent" })}
                  >
                    {DOC_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
                  </select>
                </label>
                <button
                  type="button"
                  className="button button--outline-dark"
                  disabled={remove.isPending}
                  onClick={() => { if (window.confirm(`Remove "${doc.name}" from this engagement?`)) remove.mutate({ id: doc.id }); }}
                >
                  Remove
                </button>
              </div>
            ) : (
              /* API-owned. Read-only, and it says why. */
              <em className="cp-desk__locked" title="Kept current by the PandaDoc webhook">{doc.status}</em>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

const STAGE_LABEL: Record<string, string> = {
  reminder_5: "5-day reminder",
  reminder_10: "10-day reminder",
  reminder_15: "15-day reminder",
  warning_25: "25-day suspension warning",
  suspended_30: "30-day suspension",
  referral_45: "45-day final notice",
};

/**
 * Collections for one engagement: what has been sent, and the two overrides.
 *
 * The notice history is the point of this panel. When a client says "nobody
 * told us", this is the answer — stage, date, recipients and the figures as
 * they stood when it went out.
 */
function DeskCollections({ clientId, companyName }: { clientId: number; companyName: string }) {
  const utils = trpc.useUtils();
  const history = trpc.clientPortal.adminNoticeHistory.useQuery({ clientId });
  const refresh = () => {
    void utils.clientPortal.adminNoticeHistory.invalidate({ clientId });
    void utils.clientPortal.adminList.invalidate();
  };
  const onError = (error: { message: string }) => toast.error("That did not save", { description: error.message });

  const setArrangement = trpc.clientPortal.adminSetArrangement.useMutation({
    onSuccess: () => { toast.success("Arrangement recorded — escalation is on hold."); refresh(); },
    onError,
  });
  const clearArrangement = trpc.clientPortal.adminClearArrangement.useMutation({
    onSuccess: () => { toast.success("Arrangement ended. The ladder resumes from wherever the account is."); refresh(); },
    onError,
  });
  const markReferred = trpc.clientPortal.adminMarkReferred.useMutation({
    onSuccess: () => { toast.success("Recorded as referred to Cedar Financial."); refresh(); },
    onError,
  });

  return (
    <div className="cp-desk__panel">
      <form
        className="cp-desk__form"
        onSubmit={event => {
          event.preventDefault();
          const form = event.currentTarget;
          const data = new FormData(form);
          const until = String(data.get("until") ?? "");
          if (!until) return;
          setArrangement.mutate({
            clientId,
            // Noon UTC, same reason as the financial periods: a bare date at
            // midnight renders as the previous day behind UTC.
            until: new Date(`${until}T12:00:00Z`),
            terms: String(data.get("terms") ?? "").trim(),
          });
          form.reset();
        }}
      >
        <strong className="cp-desk__form-title">Payment arrangement</strong>
        <p className="cp-desk__hint">
          Holds notices and lifts suspension until the date you set. Days past due keep counting
          underneath, so when it lapses the account is wherever it really is.
        </p>
        <div className="cp-desk__form-row">
          <label className="field"><span>In force until</span><input type="date" name="until" required /></label>
        </div>
        <label className="field">
          <span>Terms agreed</span>
          <input name="terms" required maxLength={2000} placeholder="e.g. $4,000 on the 1st and 15th until cleared" />
        </label>
        <div className="cp-desk__form-row">
          <button className="button button--coral" disabled={setArrangement.isPending}>
            {setArrangement.isPending ? "Saving…" : "Record arrangement"}
          </button>
          <button
            type="button"
            className="button button--outline-dark"
            disabled={clearArrangement.isPending}
            onClick={() => { if (window.confirm("End the arrangement now? Escalation resumes immediately.")) clearArrangement.mutate({ clientId }); }}
          >
            End arrangement
          </button>
        </div>
      </form>

      <form
        className="cp-desk__form"
        onSubmit={event => {
          event.preventDefault();
          const form = event.currentTarget;
          const reference = String(new FormData(form).get("reference") ?? "").trim();
          if (!window.confirm(`Record ${companyName} as referred to Cedar Financial? This is a record of a decision you have made — it does not send anything to Cedar.`)) return;
          markReferred.mutate({ clientId, reference });
          form.reset();
        }}
      >
        <strong className="cp-desk__form-title">Refer to Cedar Financial</strong>
        <p className="cp-desk__hint">
          Marks the account referred and stops it being flagged again. Nothing is transmitted to
          Cedar from here — send the file to them the way you normally would, then record it.
        </p>
        <label className="field">
          <span>Their reference <em>optional</em></span>
          <input name="reference" maxLength={200} />
        </label>
        <button className="button button--outline-dark" disabled={markReferred.isPending}>
          {markReferred.isPending ? "Saving…" : "Mark referred"}
        </button>
      </form>

      <strong className="cp-desk__form-title">Notices sent</strong>
      {history.isLoading && <p className="cp-desk__loading">Loading…</p>}
      {history.data?.notices.length === 0 && <p className="cp-empty">No notices have been sent on this account.</p>}
      <ul className="cp-desk__list">
        {(history.data?.notices ?? []).map(notice => (
          <li key={notice.id} data-status={notice.deliveryStatus === "sent" ? "implemented" : "open"}>
            <div>
              <strong>{STAGE_LABEL[notice.stage] ?? notice.stage}</strong>
              <small>
                {date(notice.sentAt)} · {money(notice.pastDueCents)} past due at {notice.daysPastDue} days
                {notice.deliveryStatus !== "sent" ? ` · DELIVERY ${notice.deliveryStatus.toUpperCase()}` : ""}
              </small>
              <p>To: {notice.recipients}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Training accounts                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The training desk.
 *
 * Here rather than in a separate tool because the people who run training are
 * the people who run the desk, and a second admin surface is a second thing to
 * keep behind Cloudflare Access.
 *
 * Every action is an adminProcedure that refuses a client not flagged as a
 * demo, so nothing on this panel can touch a real engagement however it is
 * clicked.
 */
function TrainingPanel() {
  const utils = trpc.useUtils();
  const status = trpc.clientPortal.adminDemoStatus.useQuery();
  const [reference, setReference] = useState("AFMG-C-TRAIN-01");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"owner" | "manager" | "billing" | "viewer">("owner");

  const refresh = () => void utils.clientPortal.adminDemoStatus.invalidate();
  const onError = (error: { message: string }) => toast.error("That did not work", { description: error.message });

  const reset = trpc.clientPortal.adminDemoReset.useMutation({
    onSuccess: result => {
      toast.success(`Training data rebuilt — ${result.reseeded.length} accounts.`);
      refresh();
    },
    onError,
  });

  const addTrainee = trpc.clientPortal.adminDemoAddTrainee.useMutation({
    onSuccess: () => {
      toast.success("Trainee added", { description: "They sign in at /clients/login with that address." });
      setEmail("");
      setName("");
      refresh();
    },
    onError,
  });

  const removeTrainee = trpc.clientPortal.adminDemoRemoveTrainee.useMutation({ onSuccess: refresh, onError });

  const accounts = status.data?.accounts ?? [];

  return (
    <section className="section cp-training">
      <div className="site-container">
        <div className="cp-training__head">
          <GraduationCap size={18} />
          <div>
            <h2>Training accounts</h2>
            <p>
              Three sample clients for staff training. They send no email, cannot reach Stripe and are
              left out of the revenue figures. The data rebuilds itself overnight.
            </p>
          </div>
          <button
            type="button"
            className="button button--navy"
            disabled={reset.isPending}
            onClick={() => reset.mutate()}
          >
            {reset.isPending ? "Rebuilding…" : accounts.length === 0 ? "Create training accounts" : "Rebuild now"}
          </button>
        </div>

        {status.isLoading && <p className="cp-desk__loading">Loading…</p>}

        {!status.isLoading && accounts.length === 0 && (
          <p className="cp-empty">
            No training accounts yet. Use “Create training accounts” — it builds all three.
          </p>
        )}

        {accounts.map(account => (
          <article key={account.reference} className="cp-training__account">
            <div className="cp-training__account-head">
              <strong>{account.companyName}</strong>
              <small>{account.reference} · {account.status.replace(/_/g, " ")}</small>
            </div>

            {account.trainees.length === 0 ? (
              <p className="cp-empty">No one can sign in to this account yet.</p>
            ) : (
              <ul className="cp-ops-users">
                {account.trainees.map(trainee => (
                  <li key={trainee.id}>
                    <div><strong>{trainee.name}</strong><small>{trainee.email} · {trainee.role}</small></div>
                    {/* The usual training-day question is "why can't I get in",
                        and the answer is almost always this column. */}
                    <em data-ok={trainee.enrolled || undefined}>
                      {trainee.enrolled ? "2FA active" : "2FA not set up"}
                    </em>
                    <button
                      type="button"
                      className="cp-desk__link"
                      onClick={() => removeTrainee.mutate({ clientUserId: trainee.id })}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </article>
        ))}

        <form
          className="cp-desk__form"
          onSubmit={event => {
            event.preventDefault();
            if (!email.trim() || !name.trim()) return;
            addTrainee.mutate({ reference, email: email.trim(), name: name.trim(), role });
          }}
        >
          <div className="cp-desk__form-row">
            <label className="field">
              <span>Account</span>
              <select value={reference} onChange={event => setReference(event.target.value)}>
                {(accounts.length ? accounts.map(a => ({ value: a.reference, label: a.companyName })) : [
                  { value: "AFMG-C-TRAIN-01", label: "TRAINING — Hudson Yards Strength Co." },
                  { value: "AFMG-C-TRAIN-02", label: "TRAINING — Brooklyn Iron Club" },
                  { value: "AFMG-C-TRAIN-03", label: "TRAINING — Astoria Performance Lab" },
                ]).map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Role</span>
              <select value={role} onChange={event => setRole(event.target.value as typeof role)}>
                <option value="owner">Owner — sees everything</option>
                <option value="manager">Manager</option>
                <option value="billing">Billing</option>
                <option value="viewer">Viewer — no billing</option>
              </select>
            </label>
          </div>
          <div className="cp-desk__form-row">
            <label className="field">
              <span>Name</span>
              <input value={name} onChange={event => setName(event.target.value)} placeholder="Jordan Ellis" />
            </label>
            <label className="field">
              <span>Work email</span>
              <input
                type="email"
                value={email}
                onChange={event => setEmail(event.target.value)}
                placeholder="jordan@altitudefitlife.com"
              />
            </label>
          </div>
          <button type="submit" className="button button--coral" disabled={addTrainee.isPending}>
            {addTrainee.isPending ? "Adding…" : "Add trainee"}
          </button>
          <p className="cp-desk__hint">
            Each trainee gets their own login: a magic link to this address, then their own authenticator.
            Sign in at <code>/clients/login</code>.
          </p>
        </form>
      </div>
    </section>
  );
}
